/**
 * Estado de la sesión: quién está conectado, en qué negocio y con qué permisos.
 *
 * Se usa desde cualquier componente:
 *
 *   const { user, tenant, puede } = useAuth();
 *   if (puede("catalog.manage")) { ...mostrar el botón de editar... }
 *
 * ⚠️ `puede()` sirve para MOSTRAR U OCULTAR botones, nada más. La seguridad real
 * la aplica el backend en cada endpoint. Ocultar un botón es comodidad visual;
 * si alguien llamara a la API a mano, el backend igual lo rechazaría.
 */

import { create } from "zustand";
import {
  negocioDelToken,
  registrarCierreDeSesion,
  registrarHuellaDeAcceso,
  registrarNegocioEsperado,
  registrarSucursalDelAparato,
  SoftfiError,
  tokens,
} from "@/lib/api";
import { invalidarConsultas, limpiarEstadoDelNegocio } from "@/lib/cambioDeNegocio";
import { auth as authApi } from "@/lib/endpoints";
import { useSucursal } from "@/stores/sucursal";
import { aviso } from "@/stores/ui";
import type { Me, Role, Session, TenantSummary, User, PlanResumen } from "@/lib/types";

// =============================================================================
//  ⚠️ LA ÚLTIMA SESIÓN BUENA, GUARDADA EN EL APARATO
// =============================================================================
//
// Es lo que permite abrir Softfi sin internet. Sin esto, recargar la página con
// la señal caída echaba al cajero a la pantalla de entrar — donde no podía
// hacer nada, porque entrar también necesita servidor.
//
// ⚠️ QUÉ SIGNIFICA GUARDAR LOS PERMISOS AQUÍ, DICHO CLARO
//
// Significa que a quien le quiten un permiso mientras está sin señal, la
// pantalla se lo va a seguir enseñando hasta que vuelva a conectar. Y está
// bien, porque **los permisos de aquí solo enseñan y esconden botones**: la
// seguridad de verdad la aplica el servidor en cada petición, y sin internet no
// hay ninguna petición que aplicar. Lo peor que puede pasar es que alguien vea
// un botón que al pulsarlo, ya con señal, le dirá que no.
//
// Va en `localStorage` y no en IndexedDB porque hace falta ANTES del primer
// dibujo y se lee de golpe, sin esperas. Es el mismo sitio donde ya viven los
// tokens.

const LLAVE_SESION = "softfi_ultima_sesion";

/**
 * ¿Esto que llegó es una sesión de verdad?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LA COMPROBACIÓN QUE PARECE DE MÁS Y NO LO ES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo encontró una prueba, y es el caso del wifi del centro comercial: el portal
 * intercepta la petición y contesta **200** con su pantalla de «acepta los
 * términos». Como el estado es bueno, esto llegaba hasta aquí con un `null`
 * dentro — y se guardaba encima de la sesión buena.
 *
 * El resultado era el fallo original otra vez, y disfrazado: el cajero se
 * conecta al wifi de la plaza «para mandar las ventas», Softfi machaca su
 * sesión guardada con basura, y a la siguiente recarga sin señal lo echa a la
 * pantalla de entrar.
 *
 * Con la comprobación, una respuesta que no trae usuario ni negocio no se
 * guarda: se conserva la última que sí lo traía.
 */
function pareceUnaSesión(yo: unknown): yo is Me {
  if (!yo || typeof yo !== "object") return false;
  const posible = yo as { user?: unknown; tenant?: unknown };
  return Boolean(posible.user) && Boolean(posible.tenant);
}

/**
 * El último negocio que tuvo esta pestaña, AUNQUE YA NO HAYA SESIÓN.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL HUECO QUE TAPA (lo vio el dueño, 2026-10-05)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Entró a Total Venezuela, salió, entró a Tiendas Moondo… y el filtro de
 * Inventario le enseñaba las categorías de Total. `aplicarSesion` solo
 * limpiaba si el negocio de ANTES era otro, y al salir `limpiar()` deja el
 * negocio en `null`: la entrada siguiente creía venir de ninguna parte y no
 * borraba nada. Lo mismo pasaba cuando la sesión caducaba sola.
 *
 * `limpiar()` no toca esto a propósito: es justo lo que hay que recordar.
 */
let ultimoNegocio: string | null = null;

// =============================================================================
//  ⚠️ LOS PERMISOS AL DÍA, SIN CERRAR SESIÓN (2026-10-19)
// =============================================================================
//
// El servidor lee los permisos de la base en cada petición; la pantalla los
// pedía una sola vez, al abrir Softfi. A quien le daban un permiso mientras
// trabajaba no le salía en el menú hasta cerrar sesión y volver a entrar (pasó
// en una capacitación: un usuario nuevo no veía los clientes), y a quien se lo
// quitaban le seguía saliendo el botón, que ya solo daba error.
//
// Ahora cada respuesta del servidor trae la huella del acceso (ver
// `registrarHuellaDeAcceso` en `api.ts`). Esta es la última que se vio; si
// llega otra, se vuelve a pedir la sesión (`actualizarAcceso`). Se olvida al
// entrar, al cambiar de negocio y al salir: la primera de cada sesión solo se
// apunta.

/** Avisa a quien guarde datos que dependen del acceso (la Caja) que cambió. */
export const ACCESO_ACTUALIZADO = "softfi:acceso-actualizado";

/** `""` = la última puesta al día falló: la próxima huella, sea cual sea, reintenta. */
let huellaConocida: string | null = null;
let accesoEnCurso: Promise<void> | null = null;
let accesoOtraVez = false;

function guardarLaSesionAquí(yo: unknown): void {
  if (!pareceUnaSesión(yo)) return;
  try {
    localStorage.setItem(LLAVE_SESION, JSON.stringify(yo));
  } catch {
    // Modo privado o almacenamiento lleno. Se sigue: lo único que se pierde es
    // poder abrir sin internet la próxima vez.
  }
}

function laSesionGuardada(): Me | null {
  try {
    const crudo = localStorage.getItem(LLAVE_SESION);
    if (!crudo) return null;
    const guardada = JSON.parse(crudo) as unknown;
    // Se comprueba también al leer: una copia escrita por una versión anterior
    // de Softfi podría no tener la forma que espera la de hoy.
    return pareceUnaSesión(guardada) ? guardada : null;
  } catch {
    return null;
  }
}

function olvidarLaSesión(): void {
  try {
    localStorage.removeItem(LLAVE_SESION);
  } catch {
    /* ver `guardarLaSesionAquí` */
  }
}

/**
 * ¿No llegamos al servidor, o el servidor dijo que no?
 *
 * `api.ts` convierte un fallo de red en un `SoftfiError` con estado 0. Un token
 * caducado o revocado llega como 401 o 403 — y ese sí se limpia, porque el
 * servidor CONTESTÓ que esa sesión ya no vale.
 *
 * Cualquier otra cosa se trata como corte de red: ante la duda, se conserva la
 * sesión. Un cajero al que se echa por error se queda sin poder cobrar; uno que
 * conserva una sesión de más no puede hacer nada que el servidor no le deje.
 */
function esUnCorteDeRed(error: unknown): boolean {
  if (!(error instanceof SoftfiError)) return true;
  return error.status !== 401 && error.status !== 403;
}

type EstadoAuth = {
  // --- Datos ---
  user: User | null;
  tenant: TenantSummary | null;
  role: Role | null;
  permissions: string[];
  tenants: TenantSummary[];

  /**
   * Qué incluye el plan del negocio activo.
   *
   *   {plan: "estandar", funciones: {equipo: false, …}, limites: {usuarios: 1}}
   *
   * ⚠️ Es para ENSEÑAR o esconder cosas, no para proteger. Quien decide de
   * verdad es el backend; esto solo evita que el usuario rellene un formulario
   * entero y se coma un 402 al final.
   */
  plan: PlanResumen | null;
  needsOnboarding: boolean;

  // --- Estado de carga ---
  /** true mientras comprobamos si hay una sesión guardada al abrir la app. */
  cargando: boolean;

  // --- Acciones ---
  aplicarSesion: (sesion: Session) => void;
  restaurarSesion: () => Promise<void>;
  /**
   * Vuelve a pedir rol, permisos y plan sin tocar nada más. La llama la huella
   * del acceso cuando cambia; ver `huellaConocida`.
   */
  actualizarAcceso: () => Promise<void>;
  /**
   * Cambia al negocio indicado y **devuelve la sesión nueva**.
   *
   * Devuelve la sesión y no `void` porque quien llama necesita saber si el
   * negocio de destino todavía tiene el wizard pendiente, para mandarlo ahí en
   * vez de a Inicio. Ver el comentario de `ModalNuevoNegocio`: adivinarlo mal
   * dejaba la pantalla en blanco.
   */
  /** Estamos dentro del negocio de un cliente, desde el panel de Softfi. */
  soporte: boolean;

  cambiarNegocio: (tenantId: string) => Promise<Session>;
  cerrarSesion: () => Promise<void>;
  limpiar: () => void;

  // --- Consultas ---
  puede: (permiso: string) => boolean;
  esDueno: () => boolean;
};

export const useAuth = create<EstadoAuth>((set, get) => ({
  user: null,
  tenant: null,
  role: null,
  permissions: [],
  tenants: [],
  plan: null,
  needsOnboarding: false,
  soporte: false,
  cargando: true,

  /**
   * Guarda una sesión recién obtenida (login, registro, cambio de negocio).
   *
   * ⚠️ Aquí está el único sitio donde se detecta que **cambió el negocio**, y
   * por eso es donde se limpia todo lo que era del anterior.
   *
   * Va aquí y no en `cambiarNegocio` porque por esta función pasan TODOS los
   * caminos que cambian de negocio, y son más de los que parece: el conmutador
   * del nombre, crear un negocio nuevo, aceptar una invitación, el selector de
   * después de iniciar sesión, y entrar con otra cuenta sin haber cerrado
   * sesión. Poniéndolo en uno solo de esos caminos, los otros cuatro seguirían
   * arrastrando el carrito y la caché del negocio de antes.
   */
  aplicarSesion: (sesion) => {
    // ⚠️ `?? ultimoNegocio`: después de salir, `tenant` ya es `null`. Ver
    // `ultimoNegocio` arriba.
    const anterior = get().tenant?.id ?? ultimoNegocio;
    const nuevo = sesion.tenant?.id;
    if (nuevo) ultimoNegocio = nuevo;
    huellaConocida = null;

    tokens.guardar(sesion.access_token, sesion.refresh_token);
    // ⚠️ La copia para poder abrir sin internet se refresca aquí también, no
    // solo al restaurar: quien entra por primera vez en un aparato y se queda
    // sin señal antes de recargar tiene que poder seguir trabajando igual.
    guardarLaSesionAquí(sesion);
    set({
      user: sesion.user,
      tenant: sesion.tenant,
      role: sesion.role,
      permissions: sesion.permissions,
      tenants: sesion.tenants,
      plan: sesion.plan ?? null,
      needsOnboarding: sesion.needs_onboarding,
      soporte: sesion.soporte === true,
      cargando: false,
    });

    // `anterior` es `null` al entrar por primera vez desde que se abrió la
    // aplicación, y ahí no hay nada que limpiar: no se viene de ningún negocio.
    if (anterior && nuevo && anterior !== nuevo) {
      void limpiarEstadoDelNegocio();
    }
  },

  /**
   * Al abrir la aplicación: si hay un token guardado, pregunta al backend quién
   * es y recupera la sesión sin pedir la contraseña.
   */
  restaurarSesion: async () => {
    if (!tokens.hay()) {
      set({ cargando: false });
      return;
    }

    try {
      const yo: Me = await authApi.yo();
      guardarLaSesionAquí(yo);
      ultimoNegocio = yo.tenant?.id ?? ultimoNegocio;
      set({
        user: yo.user,
        tenant: yo.tenant,
        role: yo.role,
        permissions: yo.permissions,
        tenants: yo.tenants,
        plan: yo.plan ?? null,
        needsOnboarding: yo.needs_onboarding,
        // ⚠️ Al recargar hay que recuperarlo, o el aviso rojo desaparece
        // mientras el token sigue apuntando al negocio del cliente.
        soporte: yo.soporte === true,
        cargando: false,
      });
    } catch (error) {
      /*
        ═══════════════════════════════════════════════════════════════════════
         ⚠️ AQUÍ SE PERDÍA EL MODO SIN CONEXIÓN ENTERO
        ═══════════════════════════════════════════════════════════════════════

        Esto borraba los tokens ante CUALQUIER fallo, y el comentario de al lado
        lo decía sin darle importancia: «el token ya no vale (o el backend está
        caído)». Son dos cosas muy distintas y la segunda es la de todos los
        días en una bodega.

        Lo que pasaba de verdad: se cae el internet, el cajero recarga la
        página —o el celular descarga la pestaña y la vuelve a cargar solo— y
        Softfi lo echa a la pantalla de entrar. Sin señal no puede iniciar
        sesión, así que se queda fuera de su propio negocio con clientes
        delante. De ahí lo de «prácticamente nada carga sin internet»: no es que
        las pantallas fallaran, es que ni siquiera había sesión.

        Ahora: si el servidor CONTESTA que el token no vale, se limpia como
        siempre. Si no se pudo ni preguntar, se sigue con la copia de la última
        sesión buena guardada en el aparato.
      */
      const guardada = laSesionGuardada();
      if (esUnCorteDeRed(error) && guardada) {
        // ⚠️ La copia y los tokens tienen que ser del mismo negocio. Si no lo
        // son (una versión anterior podía descuadrarlos), manda el del token,
        // que es con el que responde el servidor: se busca en la lista de
        // negocios de la persona. Los permisos solo muestran u ocultan botones.
        const delToken = negocioDelToken(tokens.acceso());
        const negocio =
          delToken && guardada.tenant?.id !== delToken
            ? (guardada.tenants.find((t) => t.id === delToken) ?? guardada.tenant)
            : guardada.tenant;
        ultimoNegocio = negocio?.id ?? ultimoNegocio;
        set({
          user: guardada.user,
          tenant: negocio,
          role: negocio === guardada.tenant ? guardada.role : (negocio?.role ?? guardada.role),
          permissions: guardada.permissions,
          tenants: guardada.tenants,
          plan: guardada.plan ?? null,
          needsOnboarding: guardada.needs_onboarding,
          soporte: guardada.soporte === true,
          cargando: false,
        });
        return;
      }

      tokens.borrar();
      olvidarLaSesión();
      get().limpiar();
    }
  },

  actualizarAcceso: () => {
    // Una a la vez. Si llega otra huella mientras tanto, se repite al terminar
    // para quedarse con la última.
    if (accesoEnCurso) {
      accesoOtraVez = true;
      return accesoEnCurso;
    }
    const unaVez = async () => {
      const antes = get();
      let yo: Me;
      try {
        yo = await authApi.yo();
      } catch {
        huellaConocida = "";
        return;
      }
      // Si mientras tanto cambió de negocio, de eso se encarga su propio
      // camino (`aplicarSesion`), que además limpia lo del anterior.
      if (!yo.tenant || yo.tenant.id !== antes.tenant?.id) return;

      const cambioLoQueVe =
        antes.role !== yo.role || antes.permissions.join() !== yo.permissions.join();
      guardarLaSesionAquí(yo);
      set({
        user: yo.user,
        tenant: yo.tenant,
        role: yo.role,
        permissions: yo.permissions,
        tenants: yo.tenants,
        plan: yo.plan ?? null,
        needsOnboarding: yo.needs_onboarding,
      });
      // Cada pantalla vuelve a pedir lo suyo con el acceso nuevo: las
      // sucursales que puede ver, lo que puede listar…
      invalidarConsultas();
      window.dispatchEvent(new Event(ACCESO_ACTUALIZADO));
      if (cambioLoQueVe) {
        aviso.info(
          "Sus permisos fueron actualizados",
          "El menú ya muestra las opciones a las que tiene acceso.",
        );
      }
    };
    accesoEnCurso = (async () => {
      do {
        accesoOtraVez = false;
        await unaVez();
      } while (accesoOtraVez);
    })().finally(() => {
      accesoEnCurso = null;
    });
    return accesoEnCurso;
  },

  cambiarNegocio: async (tenantId) => {
    const sesion = await authApi.cambiarNegocio(tenantId);
    get().aplicarSesion(sesion);
    return sesion;
  },

  cerrarSesion: async () => {
    const refresco = tokens.refresco();
    // Avisamos al backend para que revoque el token, pero si falla (sin internet)
    // igual cerramos la sesión aquí: el usuario pidió salir.
    if (refresco) {
      try {
        await authApi.salir(refresco);
      } catch {
        /* seguimos */
      }
    }
    tokens.borrar();
    olvidarLaSesión();
    get().limpiar();

    // ⚠️ Y lo que era del negocio, también: quien entre después puede ser de
    // otro negocio, o del mismo sin los permisos del que salió. Va después de
    // `limpiar()` —como en `aplicarSesion`— para que las pantallas ya se estén
    // yendo y no vuelvan a pedir nada sin sesión.
    try {
      await limpiarEstadoDelNegocio();
    } catch {
      // Sin IndexedDB disponible no se puede impedir salir. Lo que quede, lo
      // vuelve a limpiar `aplicarSesion` si se entra a otro negocio.
      return;
    }
    ultimoNegocio = null;
  },

  limpiar: () => {
    huellaConocida = null;
    set({
      user: null,
      tenant: null,
      role: null,
      permissions: [],
      tenants: [],
      plan: null,
      needsOnboarding: false,
      cargando: false,
    });
  },

  puede: (permiso) => get().permissions.includes(permiso),

  esDueno: () => get().role === "owner",
}));

/**
 * Si el cliente HTTP no logra renovar la sesión, limpia el estado para que el
 * router mande al login automáticamente.
 */
registrarCierreDeSesion(() => {
  useAuth.getState().limpiar();
});

/**
 * La huella del acceso de cada respuesta. Ver `huellaConocida` arriba.
 */
registrarHuellaDeAcceso((huella) => {
  if (huella === huellaConocida) return;
  const antes = huellaConocida;
  huellaConocida = huella;
  // La primera de la sesión solo se apunta: es la de lo que ya se cargó.
  if (antes === null || !useAuth.getState().user) return;
  void useAuth.getState().actualizarAcceso();
});

/**
 * La sucursal de este aparato viaja en cada petición: cada sucursal tiene su
 * catálogo (ver `registrarSucursalDelAparato` en `lib/api.ts`).
 */
registrarSucursalDelAparato(() => useSucursal.getState().elegida(useAuth.getState().tenant?.id));

/**
 * ⚠️ Si la sesión guardada es de OTRO negocio que el de la pantalla —se cambió
 * en otra pestaña—, la pestaña sigue a la sesión: se limpia lo del negocio de
 * antes y se vuelve a abrir en el Inicio, ya con el nombre y los datos del que
 * de verdad está activo. Ver `registrarNegocioEsperado` en `lib/api.ts`.
 *
 * Una recarga entera y no un arreglo a mano: así no queda nada del otro
 * negocio en ningún rincón (el carrito, la caja, la sucursal elegida…).
 */
let siguiendoLaSesion = false;
const LLAVE_ULTIMA_RECARGA = "softfi-recarga-por-negocio";
registrarNegocioEsperado(
  () => useAuth.getState().tenant?.id ?? null,
  () => {
    if (siguiendoLaSesion) return;
    // ⚠️ Nunca recargar en bucle. Si la recarga de hace un momento no lo
    // arregló —sin internet, la sesión guardada no se puede contrastar con el
    // servidor—, no se recarga otra vez: las peticiones se detienen igual, que
    // es lo que evita mostrar datos de otro negocio.
    const par = `${useAuth.getState().tenant?.id}>${negocioDelToken(tokens.acceso())}`;
    try {
      const ultima = JSON.parse(sessionStorage.getItem(LLAVE_ULTIMA_RECARGA) ?? "null") as {
        par: string;
        cuando: number;
      } | null;
      if (ultima?.par === par && Date.now() - ultima.cuando < 30_000) return;
      sessionStorage.setItem(LLAVE_ULTIMA_RECARGA, JSON.stringify({ par, cuando: Date.now() }));
    } catch {
      /* sin sessionStorage se sigue: se recarga como siempre */
    }
    siguiendoLaSesion = true;
    void limpiarEstadoDelNegocio().finally(() => window.location.replace("/"));
  },
);
